import { useState, useMemo, useEffect } from 'react';
import { useCountry } from "@/contexts/CountryContext";
import { supabase } from '@/integrations/supabase/client';
import { transformDatabaseContent, CivicEducationItem } from '@/types/civic-education';

export const useCivicEducationContent = () => {
  const [searchTerm, setSearchTerm] = useState("");
  const [activeFilter, setActiveFilter] = useState("all");
  const [activeTagFilter, setActiveTagFilter] = useState<string | null>(null);
  const [fromDate, setFromDate] = useState<Date | undefined>(undefined);
  const [toDate, setToDate] = useState<Date | undefined>(undefined);
  const { countryCode } = useCountry();
  const [contentItems, setContentItems] = useState<CivicEducationItem[]>([]);
  const [isLoading, setIsLoading] = useState(true);
  
  useEffect(() => {
    const fetchContent = async () => {
      try {
        setIsLoading(true);
        
        const { data, error } = await supabase
          .from('civic_education_content')
          .select('*')
          .eq('status', 'published')
          .order('created_at', { ascending: false });
          
        if (error) {
          console.error('Error fetching civic education content:', error);
          setContentItems([]);
          return;
        }
        
        if (data) {
          const transformedItems = data.map(item => transformDatabaseContent(item));
          setContentItems(transformedItems);
        }
      } catch (error) {
        console.error('Error loading content:', error);
        setContentItems([]);
      } finally {
        setIsLoading(false);
      }
    };
    
    fetchContent();
  }, []);
  
  const filterData = (data: CivicEducationItem[]) => {
    return data.filter(item => {
      const matchesCountry = !item.country_code || !countryCode || item.country_code === countryCode;
      
      const matchesSearch = 
        item.title.toLowerCase().includes(searchTerm.toLowerCase()) ||
        item.description?.toLowerCase().includes(searchTerm.toLowerCase()) ||
        (item.type === 'summary' ? (item as any).content?.toLowerCase().includes(searchTerm.toLowerCase()) : false);
      
      const matchesCategory = activeFilter === "all" || item.type === activeFilter;
      
      const matchesTag = !activeTagFilter || item.tags.includes(activeTagFilter);
      
      let matchesDateRange = true;
      if (fromDate || toDate) {
        const itemDate = new Date(item.date);
        if (fromDate && toDate) {
          matchesDateRange = itemDate >= fromDate && itemDate <= toDate;
        } else if (fromDate) {
          matchesDateRange = itemDate >= fromDate;
        } else if (toDate) {
          matchesDateRange = itemDate <= toDate;
        }
      }
      
      return matchesCountry && matchesSearch && matchesCategory && matchesTag && matchesDateRange;
    });
  };

  const latestTags = useMemo(() => {
    const countryContent = contentItems.filter(item => 
      !item.country_code || !countryCode || item.country_code === countryCode
    );
    const allTags = countryContent.flatMap(item => item.tags);
    const uniqueTags = Array.from(new Set(allTags));
    return uniqueTags.slice(0, 10).sort();
  }, [contentItems, countryCode]);

  const allTags = useMemo(() => {
    const countryContent = contentItems.filter(item => 
      !item.country_code || !countryCode || item.country_code === countryCode
    );
    return Array.from(new Set(countryContent.flatMap(item => item.tags))).sort();
  }, [contentItems, countryCode]);

  const filteredContent = useMemo(() => {
    if (isLoading) return { podcasts: [], videos: [], summaries: [] };
    
    const filtered = filterData(contentItems);
    
    return {
      podcasts: filtered.filter(item => item.type === 'podcast'),
      videos: filtered.filter(item => item.type === 'video'),
      summaries: filtered.filter(item => item.type === 'summary')
    };
  }, [searchTerm, activeFilter, activeTagFilter, countryCode, fromDate, toDate, contentItems, isLoading]);
  
  return {
    filteredContent,
    searchTerm,
    setSearchTerm,
    activeFilter,
    setActiveFilter,
    activeTagFilter,
    setActiveTagFilter,
    fromDate,
    setFromDate,
    toDate,
    setToDate,
    latestTags,
    allTags,
    hasContent: filteredContent.podcasts.length > 0 || 
                filteredContent.videos.length > 0 || 
                filteredContent.summaries.length > 0,
    isLoading
  };
};
